import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  AutoAwesome,
  CalendarMonth,
  AttachMoney,
  Refresh,
  Download,
  Visibility,
  Save,
  Warning,
  CheckCircle,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { SelectChangeEvent } from '@mui/material/Select';
import { recebimentoService, authService } from '../../services/api';

interface Cliente {
  id: number;
  nome: string;
  dia_atendimento: string;
  hora_atendimento: string;
  valor_atendimento: number;
  ativo: boolean;
  id_usuario: number;
}

interface MensalidadeGerada {
  id: string;
  cliente_id: number;
  cliente_nome: string;
  data_vencimento: string;
  dia_semana: string;
  valor: number;
  descricao: string;
  status: string;
}

interface FormData {
  mes: number;
  ano: number;
}

const GerarMensalidade: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });
  const [mensalidadesGeradas, setMensalidadesGeradas] = useState<MensalidadeGerada[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Obter usuário logado do localStorage
  const [usuarioLogado, setUsuarioLogado] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [recebimentosExistentes, setRecebimentosExistentes] = useState<MensalidadeGerada[]>([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [totalRegistrosGravados, setTotalRegistrosGravados] = useState(0);

  useEffect(() => {
    // Obter usuário logado
    const user = authService.getUser();
    if (user) {
      setUsuarioLogado(user);
      carregarClientesComValores(user.id);
    } else {
      // Tentar obter do localStorage com chave alternativa
      const savedUser = localStorage.getItem('gestaopsi_user');
      if (savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUsuarioLogado(parsedUser);
          carregarClientesComValores(parsedUser.id);
        } catch (error) {
          console.error('Erro ao parsear usuário do localStorage:', error);
          setError('Usuário não autenticado');
        }
      } else {
        setError('Usuário não autenticado');
      }
    }
  }, []);

  const carregarClientesComValores = async (idUsuario: number) => {
    try {
      console.log(`🔍 [DEBUG] Iniciando carregamento de clientes para usuário ID: ${idUsuario}`);
      const response: any = await recebimentoService.buscarClientesComValores(idUsuario);
      console.log(`🔍 [DEBUG] Resposta completa da API:`, response);
      
      if (response.success) {
        console.log(`✅ [DEBUG] Clientes carregados com sucesso: ${response.data.length} clientes`);
        setClientes(response.data);
      } else {
        console.error(`❌ [ERROR] Erro na resposta da API:`, response);
        setError('Erro ao carregar clientes: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error: any) {
      console.error('❌ [ERROR] Erro completo ao carregar clientes:', error);
      setError('Erro de conexão ao carregar clientes: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const handleChange = (e: SelectChangeEvent<number>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const gerarMensalidadesParaCliente = (cliente: Cliente, mes: number, ano: number) => {
    const mensalidades: MensalidadeGerada[] = [];
    
    // Mapear dias da semana para números (0 = domingo, 1 = segunda, etc.)
    const diasSemanaMap: { [key: string]: number } = {
      'domingo': 0,
      'segunda-feira': 1,
      'segunda': 1,
      'terça-feira': 2,
      'terça': 2,
      'terca-feira': 2,
      'terca': 2,
      'quarta-feira': 3,
      'quarta': 3,
      'quinta-feira': 4,
      'quinta': 4,
      'sexta-feira': 5,
      'sexta': 5,
      'sábado': 6,
      'sabado': 6
    };

    const diaAtendimentoNormalizado = cliente.dia_atendimento.toLowerCase().trim();
    const diaSemanaNumero = diasSemanaMap[diaAtendimentoNormalizado];
    
    if (diaSemanaNumero === undefined) {
      console.warn(`Dia de atendimento não reconhecido para cliente ${cliente.nome}: ${cliente.dia_atendimento}`);
      return mensalidades;
    }

    console.log(`🔍 [DEBUG] Cliente ${cliente.nome}: ${cliente.dia_atendimento} = dia da semana ${diaSemanaNumero}`);

    // Encontrar todas as ocorrências desse dia da semana no mês
    const primeiroDiaDoMes = new Date(ano, mes - 1, 1);
    const ultimoDiaDoMes = new Date(ano, mes, 0).getDate();
    
    // Set para evitar duplicatas baseado em data+cliente
    const mensalidadesUnicas = new Set<string>();
    
    for (let dia = 1; dia <= ultimoDiaDoMes; dia++) {
      const dataAtual = new Date(ano, mes - 1, dia);
      
      // Se o dia da semana coincide com o dia de atendimento do cliente
      if (dataAtual.getDay() === diaSemanaNumero) {
        const dataFormatada = dataAtual.toLocaleDateString('pt-BR');
        const chaveUnica = `${cliente.id}-${dataFormatada}`;
        
        // Verificar se já existe mensalidade para este cliente nesta data
        if (!mensalidadesUnicas.has(chaveUnica)) {
          mensalidadesUnicas.add(chaveUnica);
          
          const descricaoMensalidade = `Mensalidade - ${cliente.nome}`;

          mensalidades.push({
            id: `${cliente.id}-${dia}-${mes}-${ano}`,
            cliente_id: cliente.id,
            cliente_nome: cliente.nome,
            data_vencimento: dataFormatada,
            dia_semana: dataAtual.toLocaleDateString('pt-BR', { weekday: 'long' }),
            valor: Number(cliente.valor_atendimento || 0),
            descricao: descricaoMensalidade,
            status: 'Pendente'
          });
        } else {
          console.warn(`⚠️ [WARNING] Mensalidade duplicada evitada: ${cliente.nome} em ${dataFormatada}`);
        }
      }
    }

    console.log(`✅ [DEBUG] Cliente ${cliente.nome}: ${mensalidades.length} mensalidades únicas geradas`);
    return mensalidades;
  };

  const verificarRecebimentosExistentes = async () => {
    if (!usuarioLogado) return;
    
    // Gerar diretamente sem verificar recebimentos existentes
    procederComGeracao();
  };

  const procederComGeracao = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log(`🔍 [DEBUG] Gerando mensalidades para ${clientes.length} clientes`);
      const mensalidadesTemp: MensalidadeGerada[] = [];
      
      // Para cada cliente ativo
      clientes.filter(cliente => cliente.ativo).forEach(cliente => {
        console.log(`🔍 [DEBUG] Processando cliente: ${cliente.nome}, dia_atendimento: ${cliente.dia_atendimento}`);
        
        // Gerar mensalidades para o cliente no mês/ano especificado
        const mensalidadesCliente = gerarMensalidadesParaCliente(cliente, formData.mes, formData.ano);
        mensalidadesTemp.push(...mensalidadesCliente);
      });

      // Ordenar por data de vencimento
      mensalidadesTemp.sort((a, b) => {
        const dataA = new Date(a.data_vencimento.split('/').reverse().join('-'));
        const dataB = new Date(b.data_vencimento.split('/').reverse().join('-'));
        return dataA.getTime() - dataB.getTime();
      });

      console.log(`✅ [DEBUG] Mensalidades geradas: ${mensalidadesTemp.length} recebimentos`);
      setMensalidadesGeradas(mensalidadesTemp);
      
      // Calcular valor total
      const valorTotal = mensalidadesTemp.reduce((total, mensalidade) => total + mensalidade.valor, 0);
      
      setSuccess(`Mensalidades geradas com sucesso! ${mensalidadesTemp.length} recebimentos no valor total de R$ ${Number(valorTotal || 0).toFixed(2).replace('.', ',')}`);
      
      // Limpar mensagem de sucesso após 5 segundos
      setTimeout(() => {
        setSuccess('');
      }, 5000);
    } catch (error: any) {
      console.error('❌ [ERROR] Erro ao gerar mensalidades:', error);
      setError('Erro ao gerar mensalidades: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const gerarMensalidades = () => {
    verificarRecebimentosExistentes();
  };

  const salvarMensalidadesNoBanco = async () => {
    if (!usuarioLogado || mensalidadesGeradas.length === 0) return;
    
    // Primeiro verificar se existem recebimentos no mês
    try {
      const response: any = await recebimentoService.buscarRecebimentosPorMes(
        formData.mes, 
        formData.ano, 
        usuarioLogado.id
      );
      
      if (response.success && response.data && response.data.length > 0) {
        setRecebimentosExistentes(response.data);
        setShowConfirmDialog(true);
      } else {
        procederComSalvamento();
      }
    } catch (error: any) {
      console.error('Erro ao verificar recebimentos:', error);
      procederComSalvamento(); // Continuar mesmo com erro na verificação
    }
  };

  const procederComSalvamento = async () => {
    if (!usuarioLogado || mensalidadesGeradas.length === 0) return;
    
    setLoading(true);
    setShowConfirmDialog(false);
    
    try {
      console.log(`🔍 [DEBUG] Iniciando salvamento de ${mensalidadesGeradas.length} recebimentos`);
      
      // SEMPRE deletar recebimentos do mês antes de criar novos
      console.log(`🔍 [DEBUG] Deletando TODOS os recebimentos do mês ${formData.mes}/${formData.ano} do usuário ${usuarioLogado.id}`);
      const deleteResponse = await recebimentoService.deletarRecebimentosMes(
        formData.mes, 
        formData.ano, 
        usuarioLogado.id
      );
      console.log(`✅ [DEBUG] Resposta da deleção:`, deleteResponse);

      // Converter mensalidades para o formato esperado pelo backend
      const recebimentosParaSalvar = mensalidadesGeradas.map(mensalidade => ({
        data_vencimento: mensalidade.data_vencimento.split('/').reverse().join('-'), // Converter DD/MM/YYYY para YYYY-MM-DD
        descricao: mensalidade.descricao,
        valor: mensalidade.valor,
        status: mensalidade.status,
        id_cliente: mensalidade.cliente_id,
        forma_pagamento: '',
        observacoes: ''
      }));
      
      console.log(`🔍 [DEBUG] Recebimentos formatados para salvar:`, recebimentosParaSalvar);
      
      const response: any = await recebimentoService.criarRecebimentosMultiplos(
        recebimentosParaSalvar, 
        usuarioLogado.id
      );
      
      console.log(`🔍 [DEBUG] Resposta do salvamento:`, response);
      
      if (response.success) {
        console.log(`✅ [DEBUG] Salvamento realizado com sucesso: ${response.totalCriados || 0} registros`);
        setShowSuccessDialog(true);
        setTotalRegistrosGravados(response.totalCriados || 0);
      } else {
        console.error(`❌ [ERROR] Erro na resposta do salvamento:`, response);
        setError('Erro ao salvar mensalidades: ' + (response.message || 'Erro desconhecido'));
      }
    } catch (error: any) {
      console.error('❌ [ERROR] Erro completo ao salvar mensalidades:', error);
      setError('Erro ao salvar mensalidades: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  const limparMensalidades = () => {
    setMensalidadesGeradas([]);
    setSuccess('');
    setError('');
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  const anos = [];
  const anoAtual = new Date().getFullYear();
  for (let i = anoAtual - 1; i <= anoAtual + 2; i++) {
    anos.push(i);
  }

  // Calcular valor total das mensalidades geradas
  const valorTotal = mensalidadesGeradas.reduce((total, mensalidade) => total + mensalidade.valor, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <AttachMoney sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Gerar Mensalidades Automáticas
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Gere automaticamente as mensalidades mensais com base nos dias de atendimento e valores cadastrados para cada cliente. Todos os recebimentos do mês selecionado serão removidos.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Formulário de Geração */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonth color="primary" />
                Parâmetros das Mensalidades
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Mês</InputLabel>
                    <Select
                      name="mes"
                      value={formData.mes}
                      onChange={handleChange}
                      label="Mês"
                    >
                      {meses.map(mes => (
                        <MenuItem key={mes.value} value={mes.value}>
                          {mes.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ano</InputLabel>
                    <Select
                      name="ano"
                      value={formData.ano}
                      onChange={handleChange}
                      label="Ano"
                    >
                      {anos.map(ano => (
                        <MenuItem key={ano} value={ano}>
                          {ano}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={gerarMensalidades}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <AutoAwesome />}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Gerando...' : 'Gerar Mensalidades'}
                  </Button>
                </Grid>

                {mensalidadesGeradas.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={salvarMensalidadesNoBanco}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                        sx={{ mb: 1 }}
                      >
                        {loading ? 'Salvando...' : 'Salvar no Banco'}
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={limparMensalidades}
                        startIcon={<Refresh />}
                      >
                        Limpar Mensalidades
                      </Button>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Resultado das Mensalidades */}
        <Grid item xs={12} md={8}>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {mensalidadesGeradas.length > 0 && (
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AttachMoney color="primary" />
                    Mensalidades Geradas - {meses.find(m => m.value === formData.mes)?.label} {formData.ano}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Visualizar Mensalidades">
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Exportar Mensalidades">
                      <IconButton size="small">
                        <Download />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Data Vencimento</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Dia da Semana</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mensalidadesGeradas.map((mensalidade) => (
                        <TableRow key={mensalidade.id} hover>
                          <TableCell>{mensalidade.data_vencimento}</TableCell>
                          <TableCell>{mensalidade.dia_semana}</TableCell>
                          <TableCell>{mensalidade.cliente_nome}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                            R$ {Number(mensalidade.valor || 0).toFixed(2).replace('.', ',')}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={mensalidade.status} 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                    💰 Resumo: {mensalidadesGeradas.length} mensalidades geradas para {meses.find(m => m.value === formData.mes)?.label} de {formData.ano}
                  </Typography>
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mt: 0.5 }}>
                    💵 Valor Total: R$ {Number(valorTotal || 0).toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {mensalidadesGeradas.length === 0 && !loading && (
            <Card elevation={2}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <AttachMoney sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Nenhuma mensalidade gerada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecione o mês e ano desejados e clique em "Gerar Mensalidades" para criar automaticamente os recebimentos.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Dialog de Confirmação */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          Recebimentos Existentes
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Já existem <strong>{recebimentosExistentes.length} recebimentos</strong> para {meses.find(m => m.value === formData.mes)?.label} de {formData.ano}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deseja substituir todos os recebimentos existentes pelas novas mensalidades geradas automaticamente?
          </Typography>
          
          {recebimentosExistentes.length > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Recebimentos que serão removidos:
              </Typography>
              {recebimentosExistentes.slice(0, 5).map((recebimento, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  • {recebimento.cliente_nome || 'Cliente'} - {recebimento.data_vencimento} - R$ {Number(recebimento.valor || 0).toFixed(2).replace('.', ',')}
                </Typography>
              ))}
              {recebimentosExistentes.length > 5 && (
                <Typography variant="body2" color="text.secondary">
                  ... e mais {recebimentosExistentes.length - 5} recebimentos
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={procederComSalvamento} 
            variant="contained" 
            color="warning"
            startIcon={<Warning />}
          >
            Substituir Recebimentos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Sucesso */}
      <Dialog 
        open={showSuccessDialog} 
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'success.main', 
          color: 'success.contrastText',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CheckCircle />
          Mensalidades Salvas com Sucesso!
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box textAlign="center">
            <Typography variant="h6" sx={{ mb: 2 }}>
              {totalRegistrosGravados} recebimentos foram gravados no banco de dados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mês: {formData.mes}/{formData.ano}
            </Typography>
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mt: 1 }}>
              Valor Total: R$ {Number(valorTotal || 0).toFixed(2).replace('.', ',')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => {
              setShowSuccessDialog(false);
              // Navegar para o dashboard
              window.location.href = '/app/dashboard';
            }}
            variant="contained" 
            color="success"
            size="large"
            sx={{ minWidth: 120 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GerarMensalidade;
